import { InternalRefetchQueriesInclude, useMutation } from '@apollo/client';
import { CancelOutlined } from '@mui/icons-material';
import { Button, CircularProgress, Dialog, Grid, MenuItem, TextField, TextFieldProps } from '@mui/material';
import { WidgetContent, WidgetHeader, WidgetIcon, useCustomSnackbar } from '@procureai/ui-mui';
import { PropsWithChildren, useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { TsrctCaseCancellationReason } from '@/generated/gql/graphql';
import { Cover } from '~/components/Cover/Cover';
import { Action, Target, useTrackInteraction } from '~/hooks/useTrackInteraction/useTrackInteraction';

import { NEGOTIATION_CASE_CANCEL_MUTATION } from './NegotiationCaseCancelMutation.gql';

const commonInputProps: Partial<TextFieldProps> = {
  margin: 'dense',
  fullWidth: true,
  size: 'small',
  variant: 'outlined',
};

const REASON_OPTIONS: TsrctCaseCancellationReason[] = [
  'NO_LONGER_NEEDED' as TsrctCaseCancellationReason,
  'BUDGET_CONSTRAINT' as TsrctCaseCancellationReason,
  'SUPPLIER_UNAVAILABLE' as TsrctCaseCancellationReason,
  'DUPLICATE_CASE' as TsrctCaseCancellationReason,
  'REQUIREMENTS_CHANGED' as TsrctCaseCancellationReason,
  'AWARDED_OUTSIDE_PLATFORM' as TsrctCaseCancellationReason,
  'OTHER' as TsrctCaseCancellationReason,
];

export interface NegotiationCancelFormData {
  reason: TsrctCaseCancellationReason | null;
  message: Union.Nullable<string>;
}

export interface NegotiationCancelDialogProps {
  negotiationCaseId: string;
  /** click event for apply button */
  open: boolean;
  /** close the dialog */
  onClose: () => void;
  refetchQueries?: InternalRefetchQueriesInclude;
}

export const NegotiationCancelDialog = ({
  negotiationCaseId,
  open,
  onClose,
  refetchQueries,
}: PropsWithChildren<NegotiationCancelDialogProps>) => {
  const { t } = useTranslation();
  const track = useTrackInteraction();
  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
  const [formData, setFormData] = useState<NegotiationCancelFormData>({
    reason: null,
    message: null,
  });

  const [cancelNegotiationCase, { loading }] = useMutation(NEGOTIATION_CASE_CANCEL_MUTATION, {
    onError,
    refetchQueries,
  });

  const handleReportApply = () => {
    const sendRequest = async () => {
      await cancelNegotiationCase({
        variables: {
          id: negotiationCaseId,
          data: {
            reason: formData.reason,
            message: formData.message,
          },
        },
      });
      track(Target.NEGOTIATION_CASE, Action.CANCELLED, { id: negotiationCaseId });
      onClose();
      enqueueSuccessSnackbar(t(`NegotiationDetail.cancelSuccess`));
    };
    sendRequest();
  };

  const handleReasonChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      reason: value ? (value as TsrctCaseCancellationReason) : null,
    }));
  };

  const handleMessageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, message: event.target.value }));
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <WidgetHeader
        title={t('NegotiationCancelDialog.title')}
        icon={<WidgetIcon icon={<CancelOutlined />} />}
        actionsRight={
          <>
            <Button variant="outlined" onClick={onClose}>
              {t('NegotiationCancelDialog.cancelButton')}
            </Button>
            <Button variant="contained" color="primary" onClick={handleReportApply}>
              {t('NegotiationCancelDialog.applyButton')}
            </Button>
          </>
        }
      />
      <WidgetContent>
        <form noValidate autoComplete="off">
          <Grid
            container
            direction="row"
            spacing={3}
            sx={{
              justifyContent: 'flex-start',
              alignContent: 'flex-start',
            }}
          >
            <Grid size={{ xs: 12, sm: 12 }}>
              <TextField
                {...commonInputProps}
                select
                value={formData.reason ?? ''}
                label={t('NegotiationCancelDialog.reason.label')}
                onChange={handleReasonChange}
              >
                <MenuItem value="">{t('NegotiationCancelDialog.reason.none')}</MenuItem>
                {REASON_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {t(`CaseCancellationReason.${option}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                {...commonInputProps}
                multiline
                rows={10}
                value={formData.message ?? ''}
                label={t('NegotiationCancelDialog.message.label')}
                onChange={handleMessageChange}
              />
            </Grid>
          </Grid>
        </form>
      </WidgetContent>
      {loading ? (
        <Cover>
          <CircularProgress />
        </Cover>
      ) : null}
    </Dialog>
  );
};
